import { watch } from "vue";
import router from "../router";
import useFacility from "../components/authentication/useFacility";

function loadScript(url, attributes = {}) {
  return new Promise((resolve, reject) => {
    const head = document.head || document.getElementsByTagName("head")[0];
    const script = document.createElement("script");

    script.src = url;
    script.async = true;
    script.defer = true;

    Object.keys(attributes).forEach((key) => {
      script.setAttribute(key, attributes[key]);
    });

    head.appendChild(script);

    script.onload = resolve;
    script.onerror = reject;
  });
}

function resolveFeatureFlags(facility, facilityFeaturesMap) {
  const orgFeatures = facility?.facilityOrganization?.[0]?.features ?? [];
  return {
    smart_zones: facilityFeaturesMap?.smart_zones ?? false,
    task_management: orgFeatures.some(
      (f) => f.feature.name === "task_management" && !f.disabled
    ),
  };
}

function identifyUserPilotUser(user, facility, csOwner, featureFlags = {}) {
  if (!user || typeof window.userpilot?.identify !== "function") return;

  try {
    const traits = {
      name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      email: user.username || user.email || "",
      username: user.username || "",
      organization: facility?.facilityOrganization?.[0]?.name || "",
      created_at: user.createdAt || new Date().toISOString(),
      facility_name: facility?.name || "",
      facility_code: facility?.code || "",
      pms_provider: facility?.profile?.propertyManagementSystem || "",
      playbook: facility?.organizationDetails?.playbook || "",
      cs_account_owner: csOwner || "",
      smart_zones: featureFlags.smart_zones ? "Yes" : "No",
      task_management: featureFlags.task_management ? "Yes" : "No",
    };

    window.userpilot.identify(user.id, traits);
  } catch (error) {
    console.error("Failed to identify UserPilot user:", error);
  }
}

async function install(Vue) {
  try {
    window.userpilotSettings = {
      token: import.meta.env.VUE_APP_USER_PILOT_KEY,
    };
    window.userpilot = window.userpilot || [];

    await loadScript("https://js.userpilot.io/sdk/latest.js");

    router.afterEach(() => {
      if (window.userpilot && typeof window.userpilot.reload === "function") {
        window.userpilot.reload();
      }
    });

    const { currentFacility, csOwnerName, facilityFeatures } = useFacility();

    // Identify when facility loads (covers login + page refresh for facility users)
    watch(currentFacility, (facility) => {
      if (!facility || !window.userpilot) return;
      const userString = window.localStorage.getItem("currentUser");
      if (!userString) return;
      try {
        const user = JSON.parse(userString);
        const featureFlags = resolveFeatureFlags(
          facility,
          facilityFeatures.value
        );
        identifyUserPilotUser(user, facility, csOwnerName.value, featureFlags);
      } catch {
        console.warn("Failed to parse currentUser from localStorage");
      }
    });

    // Re-identify when CS owner name arrives (fetched async after facility loads)
    watch(csOwnerName, (ownerName) => {
      if (!ownerName || !currentFacility.value || !window.userpilot) return;
      const userString = window.localStorage.getItem("currentUser");
      if (!userString) return;
      try {
        const user = JSON.parse(userString);
        const featureFlags = resolveFeatureFlags(
          currentFacility.value,
          facilityFeatures.value
        );
        identifyUserPilotUser(
          user,
          currentFacility.value,
          ownerName,
          featureFlags
        );
      } catch {
        console.warn("Failed to parse currentUser from localStorage");
      }
    });

    setTimeout(() => {
      if (currentFacility.value || !window.userpilot) return;
      const userString = window.localStorage.getItem("currentUser");
      if (!userString) return;
      try {
        const user = JSON.parse(userString);
        identifyUserPilotUser(user, null, null);
      } catch {
        console.warn("Failed to parse currentUser from localStorage");
      }
    }, 1000);

    Vue.prototype.$userPilot = {
      identify: (user) =>
        identifyUserPilotUser(
          user,
          currentFacility.value,
          csOwnerName.value,
          resolveFeatureFlags(currentFacility.value, facilityFeatures.value)
        ),
      track: (eventName, properties = {}) => {
        if (window.userpilot && typeof window.userpilot.track === "function") {
          window.userpilot.track(eventName, properties);
        } else {
          console.warn("UserPilot track function not available");
        }
      },
      reload: () => {
        if (window.userpilot && typeof window.userpilot.reload === "function") {
          window.userpilot.reload();
        } else {
          console.warn("UserPilot reload function not available");
        }
      },
    };
  } catch (error) {
    console.warn("Failed to load UserPilot:", error);
  }
}

export default { install };
